<template>
  <path :d="d" fill="none" class="stroke-slate-400 dark:stroke-slate-600" stroke-width="1" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { path as d3path } from 'd3'
import { FlowConnection } from '../types'

/**
 * FlowConnection Component
 * 
 * Renders SVG paths between flow nodes to visualize connections.
 * Supports two connection types:
 * 
 * 1. **Linear connections**: For split nodes, creates step-like paths
 *    that clearly show branching logic with horizontal segments
 * 
 * 2. **Curved connections**: For regular node-to-node connections,
 *    creates smooth bezier curves for better visual flow
 * 
 * The path calculation adapts based on:
 * - Start and end positions
 * - Connection type (linear vs curved)
 * - Relative positioning (horizontal vs vertical)
 * 
 * This creates a consistent visual language where splits look
 * structured and regular connections flow naturally.
 */

const props = defineProps<{
  connection: FlowConnection
}>()

/**
 * Compute SVG path data
 * 
 * Uses SVG path commands from d3-path:
 * - moveTo (M)
 * - lineTo (L)
 * - bezierCurveTo (C)
 * 
 * The algorithm determines the best path shape based on the
 * connection type and relative positions of the nodes, mirroring the
 * logic from the working apps/webapp version.
 */
const d = computed(() => {
  const points = props.connection.path
  const [start, end] = points
  const type = props.connection.type
  const p = d3path()

  // Calculate the difference in x and y
  const dx = end.x - start.x
  const dy = end.y - start.y

  // Determine if the path is more horizontal or vertical
  let isHorizontal = Math.abs(dx) > Math.abs(dy)

  if (type?.includes('force') && Math.round(end.x) !== Math.round(start.x)) {
    isHorizontal = true
  }

  // Adjust these values to change the curve shape
  const curveIntensity = 0.25 // From working version

  p.moveTo(start.x, start.y)

  let startX = start.x
  let startY = start.y
  let endX = end.x
  let endY = end.y

  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    // If points are very close, draw a straight line
    p.lineTo(end.x, end.y)
  } else {
    if (isHorizontal || type === 'linear') {
      if (type === 'linear') {
        /**
         * Linear connections (for splits)
         * Creates a step-like path, mirroring the working version's d3-path usage.
         */
        if (startY !== endY) {
          const twoThirdsY = startY + ((endY - startY) * 2) / 3
          // The conditional y-value for the first segment:
          // In working version: endY - twoThirdsY > 20 ? twoThirdsY : endY
          // This seems to intend to draw to twoThirdsY unless it's too close to endY.
          // A direct translation:
          const targetYForFirstSegment = (endY - twoThirdsY > 20) || (twoThirdsY < endY) ? twoThirdsY : endY;
          p.lineTo(startX, targetYForFirstSegment)
          p.moveTo(startX, twoThirdsY) // Create a new sub-path for the horizontal segment
          p.lineTo(endX, twoThirdsY)
        } else {
          // Same height: simple horizontal line (original logic had multiple segments)
          p.lineTo(startX, endY) // Line to current y of end (which is startY)
          p.moveTo(startX, endY) // Move to start of horizontal segment at same y
          p.lineTo(endX, endY)   // Horizontal line
        }
      } else {
        /**
         * Curved connections (for regular node flow when primarily horizontal)
         * Replicates the working version's logic, including direction calculation.
         */
        const threshold = 40
        const xDelta = startX - endX // NB: xDelta is start - end
        const direction = xDelta > 0 ? 1 : -1 // Going left: 1, Going right: -1 (Matches working version)

        p.lineTo(startX, startY + threshold)
        p.moveTo(startX, startY + threshold) // Matches working version's moveTo

        p.bezierCurveTo(
          startX,
          startY + threshold,
          startX,
          startY + threshold + threshold / 2,
          startX + (threshold / 2) * direction * -1, // end x of first curve
          startY + threshold + threshold / 2  // end y of first curve
        )

        const newY = startY + threshold + threshold / 2
        const newX = endX - (threshold / 2) * direction * -1
        p.lineTo(newX, newY)

        p.bezierCurveTo(
          newX,
          newY,
          newX - (threshold / 2) * direction,
          newY,
          newX - (threshold / 2) * direction, // end x of second curve
          newY + threshold / 2               // end y of second curve
        )

        p.lineTo(endX, endY)
      }
    } else {
      // Primarily vertical connections
      // Working version had commented out Bezier logic here and defaulted to lineTo.
      // Replicating that structure:
      // let cp1x, cp1y, cp2x, cp2y;
      // cp1x = startX
      // cp1y = startY + dy * curveIntensity
      // cp2x = endX
      // cp2y = endY - dy * curveIntensity
      // p.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY)
      p.lineTo(endX, endY) // Default to straight line as in working version
    }
  }

  return p.toString()
})
</script>